'use client';

import { useState, useCallback, useMemo } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import type { ScreenName } from '../types/navigation';

interface TabBarState {
  manualOverride?: boolean;
  mainScreens: ScreenName[];
}

export function useTabBar() {
  const navigation = useNavigation();

  const currentStack = navigation.state.stacks[navigation.state.activeTab];
  const currentScreen = currentStack[currentStack.length - 1]?.screen;

  const [state, setState] = useState<TabBarState>({
    mainScreens: ['balance', 'games', 'inventory', 'shop', 'referrals', 'profile'],
  });

  const show = useCallback(() => {
    setState((prev) => ({ ...prev, manualOverride: true }));
  }, []);

  const hide = useCallback(() => {
    setState((prev) => ({ ...prev, manualOverride: false }));
  }, []);

  const clearOverride = useCallback(() => {
    setState((prev) => ({ ...prev, manualOverride: undefined }));
  }, []);

  const toggle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      manualOverride:
        prev.manualOverride === undefined ? !state.mainScreens.includes(currentScreen) : !prev.manualOverride,
    }));
  }, [currentScreen, state.mainScreens]);

  const setMainScreens = useCallback((screens: ScreenName[]) => {
    setState((prev) => ({ ...prev, mainScreens: screens }));
  }, []);

  const addMainScreen = useCallback((screen: ScreenName) => {
    setState((prev) => ({
      ...prev,
      mainScreens: prev.mainScreens.includes(screen) ? prev.mainScreens : [...prev.mainScreens, screen],
    }));
  }, []);

  const removeMainScreen = useCallback((screen: ScreenName) => {
    setState((prev) => ({
      ...prev,
      mainScreens: prev.mainScreens.filter((s) => s !== screen),
    }));
  }, []);

  const isMainScreen = state.mainScreens.includes(currentScreen);

  const visible = state.manualOverride !== undefined ? state.manualOverride : isMainScreen;

  return useMemo(
    () => ({
      visible,
      isMainScreen,
      currentScreen,
      mainScreens: state.mainScreens,
      manualOverride: state.manualOverride,

      show,
      hide,
      toggle,
      clearOverride,
      setMainScreens,
      addMainScreen,
      removeMainScreen,
    }),
    [
      visible,
      isMainScreen,
      currentScreen,
      state.mainScreens,
      state.manualOverride,
      show,
      hide,
      toggle,
      clearOverride,
      setMainScreens,
      addMainScreen,
      removeMainScreen,
    ],
  );
}
